import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { userManager } from "@core/service-container";
import { useUser } from "@domain/user/hooks/use-user";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { TertiaryButton } from "@ui/components/buttons/tertiary-button";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { NetworkError } from "@ui/components/common/error";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { RoutesPath } from "@ui/navigation/routes";
import { useMedicalProfession } from "@ui/pages/referential/use-medical-professions";
import { sendAnalyticsEventEditPassword, sendAnalyticsEventEditProfile } from "@ui/pages/settings/settings-analytics";
import { physioMeTabletBreakpoint, row, stack } from "@ui/style";
import { isNotEmpty, isNotNil } from "@utils/assert";
import React, { useCallback, useContext, useMemo } from "react";
import { useSupportedLocales } from "@domain/localization/hooks/use-supported-locales";
import { localeManager } from "@core/service-container";

import { countries } from "countries-list";
import { useCountryLanguageOptions } from "@ui/components/inputs/country-language-input/hooks/use-country-language-options";

export const AccountTab = (): JSX.Element => {
	const navigate = useNavigate();
	const dialogContext = useContext(DialogContext);
	const { format } = useI18n();
	const { formatMedicalProfession } = useMedicalProfession();
	const { isDesktop } = useDimensions();
	const user = useUser();
	const { supportedLocales } = useSupportedLocales({ localeManager });

	const GET_COUNTRY_NAME = (countryCode: string): string =>
		countries[countryCode.toUpperCase() as keyof typeof countries].native;

	const countryLanguageTraduction = useCountryLanguageOptions(
		new Map(user?.country ? [[user.country, [user.locale]]] : []), true, GET_COUNTRY_NAME)

	const userSupportedLocales = useMemo(() => {
		if (user?.country) {
			return supportedLocales.get(user.country) ?? [];
		}
		return [];
	}, [supportedLocales, user?.country]);

	const openEditPasswordDialog = useCallback(() => {
		sendAnalyticsEventEditPassword();
		dialogContext?.openModal(ModalId.EditPassword);
	}, [dialogContext]);

	const openEditProfessionalInfoDialog = useCallback(() => {
		sendAnalyticsEventEditProfile();
		dialogContext?.openModal(ModalId.EditProfessionalInformation, {
			defaultValues: {
				firstName: user?.firstName,
				lastName: user?.lastName,
				postalCode: user?.postalCode,
				cityOfPractice: user?.cityOfPractice,
			},
		});
	}, [dialogContext, user]);

	const openEditUserLocaleDialog = useCallback(() => {
		if (!user?.locale || !user.country) {
			return;
		}
		dialogContext?.openModal(ModalId.EditUserLocale, {
			currentLocale: user.locale,
			currentCountry: user.country,
			userSupportedLocales: userSupportedLocales
		});
	}, [dialogContext, user, userSupportedLocales]);

	if (!user) {
		return <NetworkError onRefresh={userManager.retrieveUser} />;
	}

	return (
		<Sections>
			<Section>
				<Title>{format("settings.accountTab.professionalInformationSection.title")}</Title>
				<SectionContent>
					<TwoColumnLayout>
						<InfoField
							label={format("settings.accountTab.professionalInformationSection.firstName")}
							value={user.firstName}
						/>
						<InfoField
							label={format("settings.accountTab.professionalInformationSection.lastName")}
							value={user.lastName}
						/>
						<InfoField
							label={format("settings.accountTab.professionalInformationSection.postalCode")}
							value={user.postalCode}
						/>
						<InfoField
							label={format("settings.accountTab.professionalInformationSection.cityOfPractice")}
							value={user.cityOfPractice}
						/>
						<InfoField
							label={format("settings.accountTab.professionalInformationSection.medicalProfession")}
							value={isNotNil(user.medicalProfession) ? formatMedicalProfession(user.medicalProfession) : undefined}
						/>
						{isDesktop && <EmptyField />}
						<InfoField
							label={format("settings.accountTab.professionalInformationSection.typeOfIdentification")}
							value={user.professionalId?.name}
						/>
						<InfoField
							label={format("settings.accountTab.professionalInformationSection.professionalNumber")}
							value={user.professionalId?.value}
						/>
					</TwoColumnLayout>
					<Button
						uiStyle="secondary"
						label={format("settings.accountTab.professionalInformationSection.editButton")}
						onClick={openEditProfessionalInfoDialog}
					/>
				</SectionContent>
			</Section>
			<Section>
				<Title>{format("settings.accountTab.credentialsSection.title")}</Title>
				<SectionContent>
					<OneColumnLayout>
						<InfoField label={format("settings.accountTab.credentialsSection.emailAddress")} value={user.email} />
						<InfoField label={format("settings.accountTab.credentialsSection.password")} value="••••••••••••" />
					</OneColumnLayout>
					<EndAlignedButton
						uiStyle="secondary"
						label={format("settings.accountTab.credentialsSection.editButton")}
						onClick={openEditPasswordDialog}
					/>
				</SectionContent>
			</Section>
			<Section>
				<Title>{format("settings.accountTab.languageSection.title")}</Title>
				<SectionContent>
					<OneColumnLayout>
						<LanguageDescription className="p-size-s-bold">{countryLanguageTraduction.get(user.locale)}</LanguageDescription>
					</OneColumnLayout>
					{userSupportedLocales.length > 1 && (
						<EndAlignedButton
							uiStyle="secondary"
							label={format("settings.accountTab.languageSection.editButton")}
							onClick={openEditUserLocaleDialog}
						/>
					)}
				</SectionContent>
			</Section>
			<Section>
				<Title>{format("settings.accountTab.deleteSection.title")}</Title>
				<Description className="p-size-s">{format("settings.accountTab.deleteSection.description")}</Description>
				<TertiaryButton
					label={format("settings.accountTab.deleteSection.cta")}
					onClick={() => navigate(RoutesPath.LeavingReasons)}
					uiStyle="danger"
				/>
			</Section>
		</Sections>
	);
};

const Sections = styled.div`
	@media ${physioMeTabletBreakpoint} {
		margin: 32px 24px;
		${stack(32, "flexstart", "stretch")}
	}
`;

const Section = styled.div``;

const Title = styled.h3`
	margin-top: 40px;
	margin-bottom: 16px;
	@media ${physioMeTabletBreakpoint} {
		margin-top: 0;
	}
`;

const SectionContent = styled.div`
	${row("L", "spacebetween", "flexstart")};
	@media ${physioMeTabletBreakpoint} {
		${stack("L", "flexstart", "stretch")}
	}
`;

const Description = styled.p`
	margin-bottom: 16px;
`;

const LanguageDescription = styled.p`
	margin-top: 15px;
`;

const OneColumnLayout = styled.div`
	display: grid;
	place-content: start;
	align-items: start;
	gap: 20px 32px;
`;

const TwoColumnLayout = styled(OneColumnLayout)`
	grid-template-columns: auto auto;
	@media ${physioMeTabletBreakpoint} {
		grid-template-columns: 1fr;
		${stack(undefined, "flexstart", "stretch")}
	}
`;

const EmptyField = styled.div``;

const EndAlignedButton = styled(Button)`
	align-self: end;
	@media ${physioMeTabletBreakpoint} {
		align-self: stretch;
	}
`;

interface InfoFieldProps {
	label: string;
	value?: string | null;
}

const InfoField = ({ label, value }: InfoFieldProps): JSX.Element => {
	const { format } = useI18n();

	return (
		<div>
			<div className="p-size-s-bold">{label}</div>
			<div>{isNotEmpty(value) ? value : format("global.empty")}</div>
		</div>
	);
};
